import * as React from "react";
import { useState, useRef, useCallback, useEffect, Component, createRef, forwardRef } from "react";
import { jsxs, jsx, Fragment } from "react/jsx-runtime";
import styled, { css } from "styled-components";
import { DropdownMenuWrapper, DropdownSize, DropdownMenuHeaderWrapper, DropdownMenuBodyWrapper, DropdownMenuOption, DropdownMenuSectionWrapper, DropdownMenuSectionTitle, DropdownMenuSectionSplit } from "@ardoq/dropdown-menu-ui";
import { useIsMounted, useResizeObserver, useEffectOnce } from "@ardoq/hooks";
import { usePreventScroll } from "@react-aria/overlays";
import { isEqual, debounce, noop } from "lodash";
import { colors, zIndex, s4, s16, s24 } from "@ardoq/design-tokens";
import { isTruthy, isMac, Portal } from "@ardoq/common-helpers";
import { IconName, ICON_SELECTOR, Icon, AnimatedChevron } from "@ardoq/icons";
import { POPOVER_WRAPPER_CLASSNAME } from "@ardoq/popovers";
import { Slider } from "@ardoq/forms";
import fscreen from "fscreen";
import { IconButton, Button } from "@ardoq/button";
import { Text } from "@ardoq/typography";
import { FlexBox } from "@ardoq/layout";
const defaultPosition = {
  get top() {
    return 0;
  },
  get left() {
    return 0;
  },
  get height() {
    return 0;
  },
  get width() {
    return 0;
  },
  get right() {
    return 0;
  },
  get bottom() {
    return 0;
  }
};
const WithDropdown = ({
  onMenuOpen,
  onMenuClose,
  children,
  requestOpen: onOpened
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hostRef = useRef(null);
  const openModal = useCallback(() => {
    setIsOpen(true);
    onMenuOpen == null ? void 0 : onMenuOpen();
  }, [onMenuOpen]);
  const closeModal = useCallback(() => {
    setIsOpen(false);
    onMenuClose == null ? void 0 : onMenuClose();
    if (hostRef.current) {
      hostRef.current.focus();
    }
  }, [onMenuClose]);
  const getHostBox = useCallback(
    () => {
      var _a;
      return ((_a = hostRef.current) == null ? void 0 : _a.getBoundingClientRect()) ?? defaultPosition;
    },
    [hostRef]
  );
  useEffect(() => {
    onOpened == null ? void 0 : onOpened.subscribe(openModal);
    return () => onOpened == null ? void 0 : onOpened.unsubscribe(openModal);
  }, [onOpened, openModal]);
  return children(openModal, closeModal, isOpen, getHostBox, hostRef);
};
const isAutomatedTestMode = process.env.NODE_ENV === "test";
const ModalBoxDiv = styled(DropdownMenuWrapper)`
  background-color: ${colors.surfaceDefault};
  left: -1000px;
  position: fixed;
  top: -1000px;
  user-select: none;
  z-index: ${zIndex.MODAL_BACKDROP + 2};
  /* prevents flash of scrollbars and dropdowns on menu load */
  pointer-events: ${isAutomatedTestMode ? "auto" : "none"};
`;
const useModalBoxStyle = (props, contentRef) => {
  const [style, setStyle] = useState();
  const calculateBoxStyle = useCallback(() => {
    var _a;
    let hostBox = props.getHostBox();
    const isInADialog = (_a = contentRef.current) == null ? void 0 : _a.closest("dialog");
    if (isInADialog) {
      const dialogBox = isInADialog.getBoundingClientRect();
      hostBox = new DOMRect(
        hostBox.left - dialogBox.left,
        hostBox.top - dialogBox.top,
        hostBox.width,
        hostBox.height
      );
    }
    const modalBox = contentRef.current ? contentRef.current.getBoundingClientRect() : defaultPosition;
    const { isScrollable = true, getPositionAndMaxHeight } = props;
    const { left, top, maxHeight, maxWidth } = getPositionAndMaxHeight(
      hostBox,
      modalBox,
      isScrollable
    );
    const newStyle = {
      top: `${top}px`,
      left: `${left}px`,
      maxHeight: `${maxHeight}px`,
      maxWidth: maxWidth ? `${maxWidth}px` : void 0,
      overflow: isScrollable ? "auto" : void 0
    };
    if (!isEqual(style, newStyle)) {
      setStyle(newStyle);
    }
  }, [contentRef, props, style]);
  useResizeObserver(contentRef, calculateBoxStyle);
  useEffectOnce(calculateBoxStyle);
  return style;
};
const ModalBox = ({
  skipAnimation,
  clickNamespace,
  testId,
  headerContent,
  footerContent,
  ...props
}) => {
  const onTransitionEnd = useCallback((e) => {
    e.currentTarget.style.pointerEvents = "auto";
  }, []);
  const contentRef = useRef(null);
  const style = useModalBoxStyle(props, contentRef);
  usePreventScroll({ isDisabled: props.isScrollLockDisabled });
  const isVisible = useIsMounted();
  return /* @__PURE__ */ jsxs(
    ModalBoxDiv,
    {
      $dropdownSize: props.dropdownSize || DropdownSize.AUTO,
      $minDropdownSize: props.minDropdownSize,
      $maxDropdownSize: props.maxDropdownSize,
      style: style || {},
      ref: contentRef,
      $isVisible: skipAnimation || isVisible || window.IS_E2E_TESTS || false,
      "data-click-namespace": clickNamespace,
      "data-test-id": testId,
      onTransitionEnd: !isAutomatedTestMode ? onTransitionEnd : void 0,
      children: [
        headerContent && /* @__PURE__ */ jsx(DropdownMenuHeaderWrapper, { children: headerContent }),
        /* @__PURE__ */ jsx(DropdownMenuBodyWrapper, { children: props.children }),
        footerContent && /* @__PURE__ */ jsx(DropdownMenuHeaderWrapper, { children: footerContent })
      ]
    }
  );
};
var HoverTarget = /* @__PURE__ */ ((HoverTarget2) => {
  HoverTarget2["NONE"] = "NONE";
  HoverTarget2["OPTION"] = "OPTION";
  HoverTarget2["BUTTON"] = "BUTTON";
  return HoverTarget2;
})(HoverTarget || {});
var DropdownOptionType = /* @__PURE__ */ ((DropdownOptionType2) => {
  DropdownOptionType2["OPTION"] = "DROPDOWN_OPTION";
  DropdownOptionType2["DIVIDER"] = "DROPDOWN_DIVIDER";
  DropdownOptionType2["SLIDER"] = "DROPDOWN_SLIDER";
  DropdownOptionType2["HEADER"] = "DROPDOWN_HEADER";
  DropdownOptionType2["MESSAGE"] = "DROPDOWN_MESSAGE";
  DropdownOptionType2["SUBMENU"] = "SUBMENU";
  DropdownOptionType2["EXPANDABLE_SECTION"] = "EXPANDABLE_SECTION";
  DropdownOptionType2["LINK"] = "LINK";
  DropdownOptionType2["BUTTON_OPTION"] = "BUTTON_OPTION";
  DropdownOptionType2["HEADER_WITH_ICON_BUTTON"] = "HEADER_WITH_ICON_BUTTON";
  DropdownOptionType2["CUSTOM_CONTENT_OPTION"] = "CUSTOM_CONTENT_OPTION";
  return DropdownOptionType2;
})(DropdownOptionType || {});
const isDropdownOption = (option) => option.type === DropdownOptionType.OPTION;
const isDropdownLink = (option) => option.type === DropdownOptionType.LINK;
const isDropdownButtonOption = (option) => option.type === DropdownOptionType.BUTTON_OPTION;
const isDropdownDivider = (option) => option.type === DropdownOptionType.DIVIDER;
const isDropdownCustomContentOption = (option) => option.type === DropdownOptionType.CUSTOM_CONTENT_OPTION;
const isDropdownHeader = (option) => option.type === DropdownOptionType.HEADER;
const isDropdownHeaderWithIconButton = (option) => option.type === DropdownOptionType.HEADER_WITH_ICON_BUTTON;
const isDropdownMessage = (option) => option.type === DropdownOptionType.MESSAGE;
const isDropdownSubmenu = (option) => option.type === DropdownOptionType.SUBMENU;
const isDropdownSliderOption = (option) => option.type === DropdownOptionType.SLIDER;
const isDropdownExpandableSection = (option) => option.type === DropdownOptionType.EXPANDABLE_SECTION;
const MIN_MARGIN_WINDOW = 20;
const MARGIN_MODAL_BOX = 2;
const getPositionAndMaxHeightFallback = (refBox, modalBox, windowHeight, windowWidth) => {
  const maxHeight = modalBox.height;
  const left = refBox.left > windowWidth - refBox.right ? refBox.left - modalBox.width - MARGIN_MODAL_BOX : refBox.right + MARGIN_MODAL_BOX;
  const top = modalBox.height > windowHeight ? MARGIN_MODAL_BOX : (windowHeight - modalBox.height) / 2;
  return { left, top, maxHeight };
};
const getPositionAndMaxHeightVertically = (refBox, modalBox, isScrollable = true, forceAlignToRight = false, verticalOffset = 0) => {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  let top = 0;
  let maxHeight = 0;
  if (windowHeight - refBox.bottom < refBox.top && modalBox.height > windowHeight - refBox.bottom - MIN_MARGIN_WINDOW) {
    top = Math.max(
      MIN_MARGIN_WINDOW,
      refBox.top - modalBox.height - MARGIN_MODAL_BOX
    ) - verticalOffset;
    maxHeight = refBox.top - MIN_MARGIN_WINDOW - MARGIN_MODAL_BOX;
  } else {
    top = refBox.bottom + MARGIN_MODAL_BOX + verticalOffset;
    maxHeight = windowHeight - refBox.bottom - MIN_MARGIN_WINDOW - MARGIN_MODAL_BOX;
  }
  const left = refBox.left + modalBox.width > windowWidth || forceAlignToRight ? refBox.right - modalBox.width : refBox.left;
  return clampToWindowBounds(
    isScrollable || modalBox.height <= maxHeight ? { left, top, maxHeight } : getPositionAndMaxHeightFallback(
      refBox,
      modalBox,
      windowHeight,
      windowWidth
    )
  );
};
const clampToWindowBounds = ({
  left,
  top,
  maxHeight
}) => {
  if (left < 0) {
    return {
      left: MIN_MARGIN_WINDOW,
      top,
      maxHeight,
      maxWidth: window.innerWidth - MIN_MARGIN_WINDOW * 2
    };
  }
  return {
    left,
    top,
    maxHeight
  };
};
const getPositionAndMaxHeightHorizontally = (refBox, modalBox, leftOffset = 0) => {
  const windowHeight = window.innerHeight;
  const windowWidth = window.innerWidth;
  let top = 0;
  let maxHeight = 0;
  if (windowHeight - refBox.top < refBox.bottom && modalBox.height > windowHeight - refBox.top - MIN_MARGIN_WINDOW) {
    top = Math.max(MIN_MARGIN_WINDOW, refBox.bottom - modalBox.height);
    maxHeight = refBox.bottom - MIN_MARGIN_WINDOW;
  } else {
    top = refBox.top;
    maxHeight = windowHeight - refBox.top - MIN_MARGIN_WINDOW;
  }
  const left = refBox.right + modalBox.width + MIN_MARGIN_WINDOW > windowWidth ? refBox.left - modalBox.width - MARGIN_MODAL_BOX : refBox.right + MARGIN_MODAL_BOX;
  return { left: left + leftOffset, top, maxHeight };
};
const navigationable = /* @__PURE__ */ new Set([
  DropdownOptionType.OPTION,
  DropdownOptionType.SUBMENU,
  DropdownOptionType.EXPANDABLE_SECTION,
  DropdownOptionType.LINK,
  DropdownOptionType.HEADER_WITH_ICON_BUTTON,
  DropdownOptionType.BUTTON_OPTION
]);
const isNavigationable = (option) => {
  if (isDropdownOption(option) && (option.isDisabled || option.forceExpand)) {
    return false;
  }
  return navigationable.has(option.type);
};
const moveCursor = (options, start, delta) => {
  let cursor = (start + options.length) % options.length;
  if (!options.some(isNavigationable)) return -1;
  while (true) {
    cursor = (cursor + options.length + delta) % options.length;
    if (isNavigationable(options[cursor])) {
      return cursor;
    }
  }
};
const inputTypes = /* @__PURE__ */ new Set(["text", "range"]);
const isFormElement = (element) => {
  return inputTypes.has(element.type);
};
const dispatchMouseEventOnTarget = (target, type = "click") => {
  const event = new MouseEvent(type, { bubbles: true });
  target.dispatchEvent(event);
};
const ModalExpandableSection = ({
  focused,
  toggleExpandedState,
  isExpanded,
  forceExpand
}) => /* @__PURE__ */ jsx(
  DropdownMenuOption,
  {
    isFocused: focused,
    isDisabled: forceExpand,
    onClick: toggleExpandedState,
    label: isExpanded ? "Less" : "More",
    rightIconName: isExpanded ? IconName.CHEVRON_UP : IconName.CHEVRON_DOWN,
    align: "right"
  }
);
class ModalSubmenu extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      position: null
    };
    this.ref = React.createRef();
    this.onMouseEnter = (event) => {
      this.openSubmenu(event);
    };
    this.onClick = (event) => {
      if (event.target === this.ref.current) {
        this.openSubmenu(event);
      }
    };
    this.onMouseLeave = (event) => {
      event.stopPropagation();
      event.preventDefault();
      this.closeMenuDebounced();
    };
    this.onMouseOver = (event) => {
      const current = this.ref.current;
      const target = event.target;
      const isOverPopover = target.closest(`.${POPOVER_WRAPPER_CLASSNAME}`);
      if (current && !(current.contains(target) || isOverPopover)) {
        this.closeMenuDebounced();
      }
    };
    this.closeMenu = () => this.setState({ isOpen: false });
    this.closeMenuDebounced = debounce(this.closeMenu, 10);
    this.getHostBox = () => {
      if (this.ref.current) {
        return this.ref.current.getBoundingClientRect();
      }
      return defaultPosition;
    };
  }
  componentDidMount() {
    document.addEventListener("mouseover", this.onMouseOver);
  }
  componentWillUnmount() {
    document.removeEventListener("mouseover", this.onMouseOver);
  }
  render() {
    const {
      name,
      label,
      options,
      focused,
      iconName,
      addKeyboardListener,
      removeKeyboardListener,
      isActive,
      isDisabled,
      popoverProps,
      isScrollable = true,
      minDropdownSize,
      maxDropdownSize,
      testId,
      intercomTarget,
      description,
      leftContent,
      leftIcon
    } = this.props;
    return /* @__PURE__ */ jsxs("div", { ref: this.ref, onMouseEnter: this.onMouseEnter, children: [
      /* @__PURE__ */ jsx(
        DropdownMenuOption,
        {
          isChecked: isActive,
          isDisabled,
          leftIconName: iconName,
          leftIcon,
          rightIcon: {
            name: IconName.CHEVRON_RIGHT
          },
          label,
          onClick: this.onMouseEnter,
          isFocused: focused || this.state.isOpen,
          testId,
          intercomTarget,
          popoverProps,
          description,
          leftContent
        },
        name
      ),
      this.state.isOpen && /* @__PURE__ */ jsx(
        ModalBox,
        {
          getHostBox: this.getHostBox,
          getPositionAndMaxHeight: (refBox, modalBox) => getPositionAndMaxHeightHorizontally(refBox, modalBox, -4),
          isScrollable,
          maxDropdownSize,
          minDropdownSize,
          isScrollLockDisabled: true,
          children: /* @__PURE__ */ jsx(
            ModalList,
            {
              options,
              addKeyboardListener,
              removeKeyboardListener,
              closeMenu: this.closeMenu
            }
          )
        }
      )
    ] });
  }
  openSubmenu(event) {
    this.closeMenuDebounced.cancel();
    event.stopPropagation();
    event.preventDefault();
    this.setState({ isOpen: true });
  }
}
const enhanceOptionsWithDefaults = (options = []) => options.map((option) => ({
  ...option,
  onClick: option.onClick || noop,
  name: option.name || "",
  label: option.label || "",
  type: option.type || DropdownOptionType.OPTION
}));
const getEventTargetRect = (event) => {
  if (!(event.target instanceof HTMLElement)) {
    return defaultPosition;
  }
  return event.target.getBoundingClientRect();
};
const simpleNotifier = () => {
  const subscriptions = [];
  return {
    subscribe: (handler) => {
      subscriptions.push(handler);
    },
    unsubscribe: (handler) => {
      const index = subscriptions.indexOf(handler);
      if (index === -1) {
        return;
      }
      subscriptions.splice(index, 1);
    },
    notify: () => {
      subscriptions.forEach((subscription) => subscription());
    }
  };
};
const getDropdownMenuPortalTarget = () => fscreen.fullscreenElement instanceof HTMLElement ? fscreen.fullscreenElement : document.body;
const SliderWrapper = styled.div`
  flex: 1 1 100%;
  margin: ${({ $hideValue, $hasRightLabel }) => {
  if ($hasRightLabel) {
    return `0 ${s4}`;
  }
  return `0 ${$hideValue ? s16 : 0} 0 ${s24}`;
}};
`;
const HeaderWithIconButtonContainer = styled.div`
  display: flex;
  ${({ $hasDarkBackground }) => $hasDarkBackground && css`
      > div {
        color: ${colors.textOnInvertedStrong};
      }
      > button {
        margin-left: auto;
      }
      background-color: ${colors.bgInvertedSubtle};
      ${ICON_SELECTOR} {
        color: ${colors.iconOnInvertedStrong};
      }
    `}
`;
const getSettingsKeyWithOptionalPath = (key, settingsPath) => (settingsPath == null ? void 0 : settingsPath.length) ? `${settingsPath.join("-")}-${key}` : key;
const isActiveOption = (option) => {
  if (option.type !== DropdownOptionType.OPTION) {
    return false;
  }
  return option.isActive ?? false;
};
const getStateFocusedOnOption = (options, isFocusedOption) => {
  const index = options.findIndex(isFocusedOption);
  return {
    cursor: index,
    hoverTarget: HoverTarget.NONE
  };
};
class ModalList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      cursor: -1,
      hoverTarget: HoverTarget.NONE,
      expandedState: {}
    };
    this.navigationOptions = [];
    this.toggleExpandableSection = {};
    this.listRef = createRef();
    this.onMouseOver = (event) => {
      const current = this.listRef.current;
      if (!(current && event.target instanceof HTMLElement)) {
        return;
      }
      const li = event.target.closest("li");
      const index = Array.from(current.children).indexOf(li);
      const hoverTarget = event.target.closest("[data-hover-target]");
      if (index !== this.state.cursor || this.state.hoverTarget === HoverTarget.NONE || this.state.hoverTarget === HoverTarget.BUTTON || hoverTarget) {
        this.setState({
          cursor: index,
          hoverTarget: hoverTarget ? HoverTarget.BUTTON : HoverTarget.OPTION
        });
      }
    };
    this.keyboardListener = (event) => {
      switch (event.key) {
        case "Tab": {
          const delta = event.shiftKey ? -1 : 1;
          this.moveCursor(delta);
          return false;
        }
        case "ArrowDown":
        case "Down":
          this.moveCursor(1);
          return false;
        case "ArrowUp":
        case "Up":
          this.moveCursor(-1);
          return false;
        case "ArrowRight":
        case "Right": {
          const option = this.navigationOptions[this.state.cursor];
          if (this.listRef.current && option && isDropdownSubmenu(option)) {
            this.dispatchClickOnFocusedOption();
            return false;
          }
          break;
        }
        case "Esc":
        case "Escape":
        case "ArrowLeft":
        case "Left":
          if (this.props.closeMenu) {
            event.preventDefault();
            event.stopPropagation();
            this.props.closeMenu();
            return false;
          }
          break;
        case "Enter": {
          const option = this.navigationOptions[this.state.cursor];
          if (this.listRef.current && option && (isDropdownOption(option) || isDropdownSubmenu(option) || isDropdownExpandableSection(option) || isDropdownHeaderWithIconButton(option) || isDropdownButtonOption(option))) {
            event.preventDefault();
            event.stopPropagation();
            this.dispatchClickOnFocusedOption();
            return false;
          }
          break;
        }
      }
      return true;
    };
    const expandedState = Object.fromEntries(
      enhanceOptionsWithDefaults(this.props.options).map((option, index) => {
        if (isDropdownExpandableSection(option)) {
          return [String(index), option.forceExpand || false];
        }
        return null;
      }).filter(isTruthy)
    );
    this.state = {
      ...this.state,
      expandedState
    };
    this.toggleExpandableSection = Object.fromEntries(
      Object.keys(expandedState).map((key) => [
        key,
        this.getToggleExpandableSectionState(key)
      ])
    );
  }
  getToggleExpandableSectionState(index) {
    return () => {
      const expandedState = {
        ...this.state.expandedState,
        [index]: !this.state.expandedState[index]
      };
      this.setState({ expandedState });
    };
  }
  updateNavigationableOptions(expandedState) {
    this.navigationOptions = enhanceOptionsWithDefaults(
      this.props.options
    ).flatMap((option, index) => {
      if (isDropdownExpandableSection(option)) {
        return [option, ...expandedState[index] ? option.options : []];
      }
      return option;
    });
  }
  moveCursor(delta) {
    this.setState(({ cursor }) => ({
      cursor: moveCursor(this.navigationOptions, cursor, delta),
      hoverTarget: HoverTarget.NONE
    }));
  }
  dispatchClickOnFocusedOption() {
    if (this.listRef.current) {
      const target = this.listRef.current.children[this.state.cursor];
      const iconButton = target && target.querySelector('[role="option"]');
      dispatchMouseEventOnTarget(iconButton || target);
    }
  }
  componentDidMount() {
    this.props.addKeyboardListener(this.keyboardListener);
    document.addEventListener("mouseover", this.onMouseOver);
    const { autoFocusActiveOption, options } = this.props;
    if (autoFocusActiveOption) {
      const focusedState = getStateFocusedOnOption(options, isActiveOption);
      this.setState(focusedState);
    }
    const focusedByDefaultOptionIndex = options.findIndex(
      (option) => option.isFocusedByDefault ?? false
    );
    if (focusedByDefaultOptionIndex > -1) {
      setTimeout(() => {
        this.setState({
          cursor: focusedByDefaultOptionIndex,
          hoverTarget: HoverTarget.NONE
        });
      }, 100);
    }
  }
  componentWillUnmount() {
    this.props.removeKeyboardListener(this.keyboardListener);
    document.removeEventListener("mouseover", this.onMouseOver);
    this.navigationOptions = [];
    this.toggleExpandableSection = {};
  }
  componentDidUpdate() {
    const listContainer = this.listRef.current;
    const scrollContainer = listContainer && listContainer.parentElement;
    const focusedItem = listContainer && listContainer.children[this.state.cursor];
    if (scrollContainer && focusedItem) {
      const containerBox = scrollContainer.getBoundingClientRect();
      const focusedBox = focusedItem.getBoundingClientRect();
      if (focusedBox.bottom > containerBox.bottom) {
        scrollContainer.scrollTop += focusedBox.bottom - containerBox.bottom;
      } else if (focusedBox.top < containerBox.top) {
        scrollContainer.scrollTop += focusedBox.top - containerBox.top;
      }
    }
  }
  render() {
    const { minDropdownSize, maxDropdownSize } = this.props;
    const { cursor, expandedState } = this.state;
    this.updateNavigationableOptions(expandedState);
    return /* @__PURE__ */ jsx(DropdownMenuSectionWrapper, { ref: this.listRef, role: "menu", children: this.navigationOptions.map((option, index) => {
      if (isDropdownOption(option)) {
        const {
          iconName = IconName.NONE,
          isActive,
          truncateLabel,
          ...rest
        } = option;
        return /* @__PURE__ */ jsx(
          DropdownMenuOption,
          {
            isFocused: index === cursor,
            leftIconName: iconName,
            isChecked: isActive,
            truncateTitle: truncateLabel,
            ...rest
          },
          `option-${index}`
        );
      }
      if (isDropdownButtonOption(option)) {
        const { iconName = IconName.NONE } = option;
        return /* @__PURE__ */ jsx(
          DropdownMenuOption,
          {
            menuOptionWrapperProps: {
              role: "button",
              "aria-label": typeof option.label === "string" ? option.label : void 0
            },
            isFocused: index === cursor,
            leftIconName: iconName,
            ...option
          },
          `button-option-${index}`
        );
      }
      if (isDropdownExpandableSection(option)) {
        const { forceExpand } = option;
        return /* @__PURE__ */ jsx(
          ModalExpandableSection,
          {
            toggleExpandedState: this.toggleExpandableSection[index],
            isExpanded: expandedState[index] || false,
            focused: index === cursor,
            forceExpand: forceExpand === true
          },
          `expandable-section-${index}`
        );
      }
      if (isDropdownSubmenu(option)) {
        return /* @__PURE__ */ jsx(
          ModalSubmenu,
          {
            addKeyboardListener: this.props.addKeyboardListener,
            removeKeyboardListener: this.props.removeKeyboardListener,
            focused: index === cursor,
            maxDropdownSize,
            minDropdownSize,
            ...option
          },
          `submenu-${index}`
        );
      }
      if (isDropdownSliderOption(option)) {
        const {
          name,
          defaultValue,
          settingsPath,
          min,
          max,
          value,
          label,
          step,
          onValueChange,
          onValueChangeEnd,
          isDisabled,
          substituteMax,
          substituteMaxLabel,
          hideValue,
          rightLabel
        } = option;
        const key = getSettingsKeyWithOptionalPath(name, settingsPath);
        return /* @__PURE__ */ jsxs(
          DropdownMenuOption,
          {
            label,
            isDisabled,
            menuOptionWrapperProps: { style: { whiteSpace: "nowrap" } },
            children: [
              /* @__PURE__ */ jsx(
                SliderWrapper,
                {
                  $hideValue: hideValue,
                  $hasRightLabel: Boolean(rightLabel),
                  children: /* @__PURE__ */ jsx(
                    Slider,
                    {
                      onChange: onValueChange,
                      onChangeEnd: onValueChangeEnd,
                      step,
                      minValue: min,
                      maxValue: max,
                      defaultValue,
                      value,
                      substituteMax,
                      substituteMaxLabel,
                      isValueLabelVisible: !hideValue && !rightLabel
                    }
                  )
                }
              ),
              rightLabel ? /* @__PURE__ */ jsx(Text, { variant: "text1", children: rightLabel }) : null
            ]
          },
          key
        );
      }
      if (isDropdownHeader(option)) {
        return /* @__PURE__ */ jsx(DropdownMenuSectionTitle, { children: option.label }, `header-${index}`);
      }
      if (isDropdownHeaderWithIconButton(option)) {
        return /* @__PURE__ */ jsxs(
          HeaderWithIconButtonContainer,
          {
            $hasDarkBackground: Boolean(option.hasDarkBackground),
            children: [
              /* @__PURE__ */ jsx(DropdownMenuSectionTitle, { children: option.label }),
              /* @__PURE__ */ jsx(
                IconButton,
                {
                  label: option.label,
                  iconName: option.iconName,
                  onClick: option.onClick
                }
              )
            ]
          },
          `header-with-icon-button-${index}`
        );
      }
      if (isDropdownMessage(option)) {
        const { label, isIndented } = option;
        return /* @__PURE__ */ jsx(
          DropdownMenuOption,
          {
            label,
            isChecked: isIndented ? false : void 0
          },
          `message-option-${index}`
        );
      }
      if (isDropdownLink(option)) {
        const { label, link, clickId, intercomTarget } = option;
        return /* @__PURE__ */ jsx(
          DropdownMenuOption,
          {
            label: label || "Read more",
            intercomTarget,
            clickId,
            onClick: () => window.open(link, "_blank"),
            rightIconName: IconName.OPEN_IN_NEW
          },
          `link-option-${index}`
        );
      }
      if (isDropdownCustomContentOption(option)) {
        return option.content;
      }
      if (isDropdownDivider(option)) {
        return /* @__PURE__ */ jsx(DropdownMenuSectionSplit, {}, `divider-${index}`);
      }
    }) });
  }
}
const ModalBackdropDiv = styled.div`
  bottom: 0;
  left: 0;
  position: fixed;
  right: 0;
  top: 0;
  z-index: ${zIndex.MODAL_BACKDROP};
  &:focus {
    outline: none;
  }
`;
const preventContextMenu = (event) => {
  event.stopPropagation();
};
class ModalBackdrop extends Component {
  constructor() {
    super(...arguments);
    this.contentRef = createRef();
    this.backdropRef = createRef();
    this.mouseDownTarget = null;
    this.listeners = [];
    this.skipCloseModal = false;
    this.onBackdropClick = (event) => {
      if (!this.isClick(event)) {
        return;
      }
      event.stopPropagation();
      const { isKeepOpen, closeModal } = this.props;
      if (!(isKeepOpen && this.isContentClick(event))) {
        closeModal();
      }
    };
    this.onBackdropMouseDown = (event) => {
      this.mouseDownTarget = event.target;
    };
    this.addKeyboardListener = (listener) => {
      this.listeners.unshift(listener);
    };
    this.removeKeyboardListener = (listener) => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
    this.onKeyboardDown = (event) => {
      this.ensureFocusInBackdrop();
      if (event.key === "f" && (isMac() ? event.metaKey : event.ctrlKey)) {
        this.skipCloseModal = true;
        return;
      }
      switch (event.key) {
        case "ArrowDown":
        case "ArrowLeft":
        case "ArrowRight":
        case "ArrowUp":
        case "Down":
        case "Left":
        case "Right":
        case "Up":
          if (isFormElement(event.target)) {
            break;
          }
        // else fall through
        case "End":
        case "Home":
        case "PageDown":
        case "PageUp":
        case "Tab":
          event.preventDefault();
          break;
      }
      if (this.listeners.every((listener) => listener(event))) {
        switch (event.key) {
          case "Esc":
          case "Escape":
            this.closeModal();
            event.preventDefault();
            event.stopPropagation();
            return;
        }
      }
    };
    this.closeModal = () => this.props.closeModal();
    this.maybeCloseModal = () => {
      if (this.skipCloseModal) this.skipCloseModal = false;
      else this.closeModal();
    };
  }
  componentDidMount() {
    if (!this.props.ignoreFocusTrap) {
      this.ensureFocusInBackdrop();
    }
    window.addEventListener("resize", this.closeModal);
    window.addEventListener("blur", this.maybeCloseModal);
    if (!this.props.ignoreKeyboardEvents) {
      document.addEventListener("keydown", this.onKeyboardDown, true);
    }
  }
  componentWillUnmount() {
    this.listeners.length = 0;
    window.removeEventListener("resize", this.closeModal);
    window.removeEventListener("blur", this.maybeCloseModal);
    document.removeEventListener("keydown", this.onKeyboardDown, true);
  }
  render() {
    var _a, _b;
    return /* @__PURE__ */ jsx(
      ModalBackdropDiv,
      {
        ref: this.backdropRef,
        onClick: this.onBackdropClick,
        onMouseDown: this.onBackdropMouseDown,
        onContextMenu: preventContextMenu,
        tabIndex: 1,
        "data-click-namespace": this.props.clickNamespace,
        children: /* @__PURE__ */ jsx("div", { ref: this.contentRef, children: (_b = (_a = this.props).children) == null ? void 0 : _b.call(
          _a,
          this.addKeyboardListener,
          this.removeKeyboardListener
        ) })
      }
    );
  }
  isClick(event) {
    const isClick = !event.isTrusted || event.target === this.mouseDownTarget;
    this.mouseDownTarget = null;
    return isClick;
  }
  isContentClick(event) {
    var _a;
    return (_a = this.contentRef.current) == null ? void 0 : _a.contains(event.target);
  }
  ensureFocusInBackdrop() {
    if (this.backdropRef.current && !this.backdropRef.current.contains(document.activeElement)) {
      this.backdropRef.current.focus();
    }
  }
}
const OptionalPortal$1 = ({ children, disablePortal }) => disablePortal ? /* @__PURE__ */ jsx(Fragment, { children }) : /* @__PURE__ */ jsx(Portal, { to: getDropdownMenuPortalTarget(), children });
const ButtonWithDropdownTemplate = ({
  options,
  footerContent,
  headerContent,
  isKeepOpen = false,
  alignMenuTo = "left",
  dropdownStyle,
  iconStyle,
  disablePortal,
  dropdownSize,
  minDropdownSize,
  maxDropdownSize,
  onMenuOpen,
  onMenuClose,
  clickNamespace,
  requestOpen: onOpened,
  renderButton,
  isOpen: externallyManagedIsOpen,
  modalBoxVerticalOffset = 4
}) => {
  const forceAlignMenuToRight = alignMenuTo !== "left";
  return /* @__PURE__ */ jsx(
    WithDropdown,
    {
      onMenuOpen,
      onMenuClose,
      requestOpen: onOpened,
      children: (openModal, closeModal, isOpen, getHostBox, hostRef) => {
        return /* @__PURE__ */ jsxs(Fragment, { children: [
          renderButton(
            hostRef,
            externallyManagedIsOpen ?? isOpen,
            openModal
          ),
          (externallyManagedIsOpen ?? isOpen) && /* @__PURE__ */ jsx(OptionalPortal$1, { disablePortal, children: /* @__PURE__ */ jsx(ModalBackdrop, { closeModal, isKeepOpen, children: (addKeyboardListener, removeKeyboardListener) => /* @__PURE__ */ jsx(
            ModalBox,
            {
              footerContent,
              headerContent,
              clickNamespace,
              dropdownSize,
              minDropdownSize,
              maxDropdownSize,
              getHostBox,
              getPositionAndMaxHeight: (refBox, modalBox) => getPositionAndMaxHeightVertically(
                refBox,
                modalBox,
                true,
                forceAlignMenuToRight,
                modalBoxVerticalOffset
              ),
              children: /* @__PURE__ */ jsx(
                ModalList,
                {
                  options,
                  addKeyboardListener,
                  removeKeyboardListener,
                  iconStyle,
                  dropdownStyle
                }
              )
            }
          ) }) })
        ] });
      }
    }
  );
};
const DropdownButton = ({
  iconName,
  hideChevron,
  isOpen,
  onClick,
  isDisabled,
  iconSize,
  tooltipText,
  hostRef,
  isActive,
  children,
  ...props
}) => /* @__PURE__ */ jsxs(
  Button,
  {
    onClick,
    "data-tooltip-text": tooltipText,
    "data-is-active": isOpen,
    "data-is-disabled": isDisabled,
    ref: hostRef,
    isActive: isActive || isOpen,
    isDisabled,
    ...props,
    children: [
      iconName && /* @__PURE__ */ jsx(Icon, { iconName, iconSize }),
      children,
      !hideChevron && !iconName && /* @__PURE__ */ jsx(AnimatedChevron, { $isUp: isOpen })
    ]
  }
);
const ButtonWithDropdown = ({
  options,
  children,
  footerContent,
  headerContent,
  isKeepOpen = false,
  isDisabled = false,
  alignMenuTo = "left",
  dropdownStyle,
  iconStyle,
  tooltipText,
  hideChevron,
  disablePortal,
  dropdownSize,
  minDropdownSize,
  maxDropdownSize,
  iconName,
  iconSize,
  onMenuOpen,
  onMenuClose,
  clickNamespace,
  isActive,
  requestOpen,
  onClick,
  isOpen: externallyManagedIsOpen,
  modalBoxVerticalOffset,
  renderDropdownButton,
  ...props
}) => /* @__PURE__ */ jsx(
  ButtonWithDropdownTemplate,
  {
    renderButton: renderDropdownButton ? renderDropdownButton : (hostRef, isOpen, openModal) => /* @__PURE__ */ jsx(
      DropdownButton,
      {
        onClick: (e) => {
          e.stopPropagation();
          onClick == null ? void 0 : onClick();
          return options.length ? openModal() : void 0;
        },
        hostRef,
        isActive,
        isDisabled,
        iconName,
        iconSize,
        hideChevron,
        isOpen: externallyManagedIsOpen ?? isOpen,
        tooltipText,
        ...props,
        children
      }
    ),
    options,
    footerContent,
    headerContent,
    isKeepOpen,
    alignMenuTo,
    dropdownStyle,
    iconStyle,
    disablePortal,
    dropdownSize,
    minDropdownSize,
    maxDropdownSize,
    onMenuOpen,
    onMenuClose,
    clickNamespace,
    requestOpen,
    isOpen: externallyManagedIsOpen,
    modalBoxVerticalOffset
  }
);
const OptionalPortal = ({ children, disablePortal }) => disablePortal ? /* @__PURE__ */ jsx(Fragment, { children }) : /* @__PURE__ */ jsx(Portal, { children });
const ComponentWithDropdown = ({
  options,
  isKeepOpen = false,
  alignMenuTo = "left",
  dropdownStyle,
  iconStyle,
  disablePortal,
  dropdownSize,
  Component: Component2,
  componentProps,
  headerContent,
  footerContent,
  autoFocusActiveOption,
  onMenuClose,
  onMenuOpen
}) => {
  const forceAlignMenuToRight = alignMenuTo !== "left";
  return /* @__PURE__ */ jsx(WithDropdown, { children: (openModal, closeModal, isOpen, getHostBox, hostRef) => /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      Component2,
      {
        ref: hostRef,
        onMenuOpen: () => {
          openModal();
          if (onMenuOpen) onMenuOpen();
        },
        isMenuOpened: isOpen,
        ...componentProps
      }
    ),
    isOpen && /* @__PURE__ */ jsx(OptionalPortal, { disablePortal, children: /* @__PURE__ */ jsx(
      ModalBackdrop,
      {
        closeModal: () => {
          closeModal();
          if (onMenuClose) onMenuClose();
        },
        isKeepOpen,
        children: (addKeyboardListener, removeKeyboardListener) => /* @__PURE__ */ jsx(
          ModalBox,
          {
            dropdownSize,
            getHostBox,
            headerContent,
            footerContent,
            getPositionAndMaxHeight: (refBox, modalBox) => getPositionAndMaxHeightVertically(
              refBox,
              modalBox,
              true,
              forceAlignMenuToRight
            ),
            children: /* @__PURE__ */ jsx(
              ModalList,
              {
                options,
                addKeyboardListener,
                removeKeyboardListener,
                iconStyle,
                dropdownStyle,
                autoFocusActiveOption
              }
            )
          }
        )
      }
    ) })
  ] }) });
};
const DropdownTrigger = forwardRef(
  ({ onMenuOpen, children, WrapperComponent, isDisabled }, ref) => /* @__PURE__ */ jsx(
    WrapperComponent,
    {
      ref,
      onClick: !isDisabled ? onMenuOpen : void 0,
      role: "button",
      tabIndex: 0,
      style: { cursor: "pointer" },
      "aria-disabled": isDisabled,
      children
    }
  )
);
DropdownTrigger.displayName = "DropdownTrigger";
const NavigationDropdown = ({
  navigationOptions,
  children,
  WrapperComponent = FlexBox,
  isDisabled = false
}) => {
  return /* @__PURE__ */ jsx(
    ComponentWithDropdown,
    {
      options: navigationOptions,
      Component: (props) => /* @__PURE__ */ jsx(
        DropdownTrigger,
        {
          ...props,
          WrapperComponent,
          isDisabled,
          children
        }
      )
    }
  );
};
const SplitButton = styled(Button)`
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
`;
const ChevronButton = styled(Button)`
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: 2px;
`;
const SplitDropdownButton = ({
  iconName,
  isOpen,
  onSplitButtonClick,
  onChevronButtonClick,
  isDisabled,
  isSplitButtonDisabled,
  isSplitButtonActive,
  iconSize,
  tooltipText,
  hostRef,
  isActive,
  mainButtonDataTestId,
  dropdownButtonDataTestId,
  children,
  dropdownButtonAriaLabel,
  ...props
}) => /* @__PURE__ */ jsxs("div", { children: [
  /* @__PURE__ */ jsxs(
    SplitButton,
    {
      onClick: onSplitButtonClick,
      isDisabled: isSplitButtonDisabled,
      isActive: isSplitButtonActive,
      dataTestId: mainButtonDataTestId,
      ...props,
      children: [
        iconName && /* @__PURE__ */ jsx(Icon, { iconName, iconSize }),
        children
      ]
    }
  ),
  /* @__PURE__ */ jsx(
    ChevronButton,
    {
      onClick: onChevronButtonClick,
      "data-tooltip-text": tooltipText,
      "data-is-active": isOpen,
      "data-is-disabled": isDisabled,
      ref: hostRef,
      isActive: isActive || isOpen,
      isDisabled,
      dataTestId: dropdownButtonDataTestId,
      "aria-label": dropdownButtonAriaLabel,
      ...props,
      children: /* @__PURE__ */ jsx(AnimatedChevron, { $isUp: isOpen })
    }
  )
] });
const SplitButtonWithDropdown = ({
  options,
  footerContent,
  headerContent,
  isKeepOpen = false,
  isDisabled = false,
  alignMenuTo = "left",
  dropdownStyle,
  iconStyle,
  tooltipText,
  disablePortal,
  dropdownSize,
  minDropdownSize,
  maxDropdownSize,
  onMenuOpen,
  onMenuClose,
  clickNamespace,
  isActive,
  iconName,
  iconSize,
  children,
  isSplitButtonActive,
  isSplitButtonDisabled,
  onSplitButtonClick,
  requestOpen,
  onChevronButtonClick,
  mainButtonDataTestId,
  dropdownButtonDataTestId,
  dropdownButtonAriaLabel,
  ...props
}) => /* @__PURE__ */ jsx(
  ButtonWithDropdownTemplate,
  {
    renderButton: (hostRef, isOpen, openModal) => /* @__PURE__ */ jsx(
      SplitDropdownButton,
      {
        onChevronButtonClick: () => {
          onChevronButtonClick == null ? void 0 : onChevronButtonClick();
          return options.length ? openModal() : void 0;
        },
        hostRef,
        isActive,
        isDisabled,
        isSplitButtonActive,
        isSplitButtonDisabled,
        onSplitButtonClick,
        iconName,
        iconSize,
        isOpen,
        tooltipText,
        mainButtonDataTestId,
        dropdownButtonDataTestId,
        dropdownButtonAriaLabel,
        ...props,
        children
      }
    ),
    options,
    footerContent,
    headerContent,
    isKeepOpen,
    alignMenuTo,
    dropdownStyle,
    iconStyle,
    disablePortal,
    dropdownSize,
    minDropdownSize,
    maxDropdownSize,
    onMenuOpen,
    onMenuClose,
    clickNamespace,
    requestOpen
  }
);
const ContextMenuProvider = ({
  dropdownSize,
  children,
  options
}) => {
  const [currentOptions, setCurrentOptions] = useState(options || []);
  const [hostBox, setHostBox] = useState(defaultPosition);
  const [closeCb, setCloseCb] = useState(null);
  if (!children) return null;
  return /* @__PURE__ */ jsx(WithDropdown, { children: (openModal, closeModal, isOpen, getHostBox) => {
    const wrappedCloseModal = () => {
      closeModal();
      closeCb == null ? void 0 : closeCb();
      setCloseCb(null);
    };
    return /* @__PURE__ */ jsxs(Fragment, { children: [
      children({
        isOpen,
        openContextMenu: (event, { options: options2, onClose }) => {
          if (options2) setCurrentOptions(options2);
          setHostBox(getEventTargetRect(event));
          setCloseCb(() => onClose);
          openModal();
        },
        closeContextMenu: wrappedCloseModal
      }),
      isOpen && /* @__PURE__ */ jsx(Portal, { to: getDropdownMenuPortalTarget(), children: /* @__PURE__ */ jsx(ModalBackdrop, { closeModal: wrappedCloseModal, children: (addKeyboardListener, removeKeyboardListener) => /* @__PURE__ */ jsx(
        ModalBox,
        {
          dropdownSize,
          getHostBox,
          getPositionAndMaxHeight: (_refBox, modalBox, isScrollable) => getPositionAndMaxHeightVertically(
            hostBox,
            modalBox,
            isScrollable
          ),
          children: /* @__PURE__ */ jsx(
            ModalList,
            {
              options: enhanceOptionsWithDefaults(currentOptions),
              addKeyboardListener,
              removeKeyboardListener
            }
          )
        }
      ) }) })
    ] });
  } });
};
export {
  ButtonWithDropdown,
  ComponentWithDropdown,
  ContextMenuProvider,
  DropdownOptionType,
  HoverTarget,
  ModalBackdrop,
  ModalBox,
  ModalBoxDiv,
  ModalList,
  NavigationDropdown,
  SplitButtonWithDropdown,
  WithDropdown,
  dispatchMouseEventOnTarget,
  enhanceOptionsWithDefaults,
  getDropdownMenuPortalTarget,
  getPositionAndMaxHeightHorizontally,
  getPositionAndMaxHeightVertically,
  isDropdownOption,
  isDropdownSubmenu,
  isFormElement,
  moveCursor,
  simpleNotifier
};
//# sourceMappingURL=index.js.map
